// Тема по выбору ученика: поиск, выбор и три разных «решать».
//
// **Зачем отдельный путь, когда рядом есть «Ещё задачи».** Добавка берёт то,
// что должно сейчас по расписанию повторений, и тему не спрашивает: ученик,
// пришедший за НОД и НОК, получает от неё что угодно, кроме НОД и НОК. Это
// ровно тот случай, ради которого решение 2 держит автономию единственной
// мотивационной потребностью с приличным эффектом у занимающегося одного.
//
// **Зачем на «Сегодня», когда та же кнопка есть на «Прогрессе».** Там она
// стоит четвёртой в карточке цели и зачёта — рядом с выбором цели, зачётом,
// владением и свидетельством под ним. Всё это про карту, а не про «хочу
// сейчас порешать НОД». Экран «Сегодня» отвечает на один вопрос — что делать
// сейчас, — и выбор темы это тот же вопрос, заданный учеником, а не системой.
//
// **В списке все темы с задачами, а не только открытые.** Спрятать закрытые
// значило бы на вопрос «почему НОД и НОК нет в списке» не ответить ничем:
// поиск, который молча не находит, читается как поломка, а не как правило.
// Закрытая тема находится и говорит, чем она открывается, — зачётом, и он тут
// же. Тот же довод, по которому `SkillPicker` не обрезает список до двадцати
// строк. С 16 сентября 2026 у неё есть и чем заняться до зачёта: тренажёр —
// задачи по теме подряд, с разбором, не идущие в счёт дня и тему не
// открывающие. До этого закрытая тема предлагала только экзамен по себе.
//
// **Два глагола на одном поиске, с 14 сентября 2026.** «Решать» добавляет три
// задачи сверх набора и набор не трогает; «Весь день на эту тему» пересобирает
// день целиком. Второй поиск под второй глагол завёл бы на экране два
// одинаковых поля в одной колонке — и вопрос «а чем они отличаются» вместо
// ответа на «что делать сейчас».

import { useState } from "react";
import { api, useMutation, type DayPlan, type ProgressSnapshot } from "../api";
import { reasonOf } from "../lib/errors";
import { plural } from "../lib/dates";
import { SkillPicker } from "./SkillPicker";

export function PracticeCard({
  learnerKey,
  plan,
  catalog,
  sections,
  onStart,
  onOpenTheory,
  onDrill,
}: {
  learnerKey: string;
  plan: DayPlan;
  catalog: ProgressSnapshot["catalog"];
  sections: ProgressSnapshot["sections"];
  /** Задачи добавлены — экран уходит прямо в первую, без остановки на наборе. */
  onStart: (itemKey: string) => void;
  onOpenTheory: (skillId: string, open: boolean) => void;
  /** Тренажёр по теме. Единственная кнопка карточки, которой всё равно,
      открыта тема или нет: он не двигает ни набора, ни владения. */
  onDrill: (skillId: string) => void;
}) {
  const practice = useMutation(api.today.practice);
  const ensurePlan = useMutation(api.today.ensurePlan);
  const startTestOut = useMutation(api.testout.start);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const named = new Map(sections.map((s) => [s.id, s.name]));
  // Темы без задач в пуле не показываются вовсе: у них нечего решать и нечем
  // сдавать зачёт, то есть обе кнопки карточки погашены. Строка, у которой
  // нет ни одного действия, — это не ответ на поиск, а тупик.
  const items = catalog
    .filter((c) => c.covered)
    .map((c) => ({
      id: c.skillId,
      name: c.name,
      hint: named.get(c.section) ?? c.section,
    }));
  const chosen = picked === null ? null : (catalog.find((c) => c.skillId === picked) ?? null);

  // Режим дня меняется, только пока не закрыта ни одна задача. Правило не
  // здешнее: его держит `today.ensurePlan` для всех трёх режимов сразу, а
  // экран лишь не предлагает того, что заведомо откажет. Причина та же, по
  // которой её завели для короткого режима, — плохой день выясняется в начале,
  // а не посередине.
  const locked = plan.assignments.some((a) => a.done);
  const topicDay = plan.topic;
  // Цена режима, числом и до нажатия: столько повторений из сегодняшнего
  // набора уедут на завтра. Не «долгов вообще» из обзора карты — здесь важно
  // ровно то, что лежит на столе сейчас.
  const reviewsLeft = plan.assignments.filter((a) => a.kind === "review" && !a.done).length;

  const run = (action: Promise<unknown>, then: (r: unknown) => void): void => {
    setBusy(true);
    setFailed(null);
    void action
      .then((r) => {
        setBusy(false);
        then(r);
      })
      .catch((e: unknown) => {
        setBusy(false);
        // `reasonOf`, а не `e.message`: в проде Convex вырезает сообщение
        // обычного Error, и «тема закрыта» доехало бы как «Server Error».
        setFailed(reasonOf(e));
      });
  };

  return (
    <div className="card">
      <div className="card__label">Своя тема</div>

      {/*
        День уже идёт по теме — карточка сначала говорит это, и только потом
        предлагает искать: поиск иначе читался бы как «выбери тему» там, где
        она выбрана.

        Выхода из режима здесь нет намеренно. Смена режима дня живёт в одном
        месте — строкой под кнопкой «Начать», рядом с коротким режимом; здесь
        только вход, и только потому, что выбор темы без поиска не сделать.
      */}
      {topicDay === null ? null : (
        <div className="notice" style={{ marginTop: 12 }}>
          Сегодня весь день по теме «{topicDay.name}». Повторения по другим
          темам не придут, новый навык не открывается.
          {locked ? " День начат — набор сегодня уже не пересобирается." : null}
        </div>
      )}

      {/*
        Оба глагола названы ДО того, как тема выбрана. Здесь стояло «Сверх
        набора, порцией из трёх задач. Набор от этого не меняется» — то есть
        карточка прямо обещала, что набор не тронется, и про день по теме не
        говорила вовсе. Кнопки появляются только после выбора темы в поиске, и
        режим дня получался невидимым: чтобы узнать, что он есть, надо было
        сначала что-то найти. Найдено на экране 14 сентября 2026.
      */}
      <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
        {locked ? (
          // День начат — второго глагола не будет, и обещать его нельзя: это
          // та же ошибка, что была в прежнем тексте, только в зеркале.
          <>
            Найди тему и реши по ней три задачи сверх набора. Отдать ей весь день
            можно было до первой закрытой задачи — режим дня не пересобирается на
            середине.
          </>
        ) : (
          <>
            Найди тему — и реши по ней три задачи сверх набора или отдай ей весь
            день вместо набора.
          </>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <SkillPicker
          items={items}
          value={picked}
          onPick={(id) => {
            setPicked(id);
            setFailed(null);
          }}
          placeholder="найти тему"
        />
      </div>

      {chosen === null ? null : (
        <>
          {/*
            Одна строка состояния вместо трёх меток. На «Прогрессе» под темой
            стоят владение, свидетельство и происхождение числа — там это по
            делу, экран про карту. Здесь нужен ровно один факт: можно решать
            или ещё нет.
          */}
          {!chosen.open ? (
            <div className="faint" style={{ marginTop: 10, lineHeight: 1.5 }}>
              Тема ещё закрыта: её предпосылки не пройдены. Порешать её можно в
              тренажёре — задачи подряд, с разбором после каждой, но в счёт дня
              они не идут и тему не открывают. Открыть можно зачётом — три
              задачи посложнее, без подсказок.
            </div>
          ) : null}

          <div className="row row--actions" style={{ marginTop: 12 }}>
            {chosen.open ? (
              <button
                className="btn btn--primary"
                disabled={busy}
                onClick={() =>
                  run(practice({ learnerKey, skillId: chosen.skillId }), (r) =>
                    onStart((r as { firstItemKey: string }).firstItemKey),
                  )
                }
              >
                Решать
              </button>
            ) : (
              // У ЗАКРЫТОЙ темы главная кнопка — тренажёр, а не зачёт, и
              // порядок здесь не вкусовой. До 16 сентября 2026 закрытая тема
              // предлагала ровно одно: «Сдать зачёт», то есть экзамен, —
              // притом что в список её кладут как раз затем, чтобы порешать
              // тему, до которой планировщик не дошёл. Зачёт остался рядом,
              // второй кнопкой: он единственный тему открывает.
              <button
                className="btn btn--primary"
                disabled={busy}
                onClick={() => onDrill(chosen.skillId)}
              >
                Тренажёр
              </button>
            )}

            {/*
              Зачёт забирает экран сам: `testout.current` — живой запрос, и
              App.tsx уводит в него, как только документ появился. Своего
              перехода здесь не нужно.
            */}
            {chosen.open ? null : (
              <button
                className="btn"
                disabled={busy || chosen.credited}
                onClick={() =>
                  run(startTestOut({ learnerKey, skillId: chosen.skillId }), () => {})
                }
              >
                Сдать зачёт
              </button>
            )}

            {/*
              Второй глагол виден, только когда он выполним: тема открыта, день
              ещё не начат и это не та же тема, по которой день уже идёт.
              Кнопка, которая на нажатие объясняет, почему нажимать было
              нельзя, — хуже отсутствующей.
            */}
            {/*
              «Разобраться» стоит перед «весь день», а не после: прочитать
              правило перед двенадцатью задачами по теме — обычный порядок, а
              не запасной выход. Кнопки нет только там, где разбор не написан.

              У ЗАКРЫТОЙ темы она есть, и это не недосмотр. Тема закрыта — это
              про карту: предпосылки по её меркам не пройдены. Читать правило
              это не запрещает, а ровно наоборот — сын проходит тему в школе
              сейчас, и разбор нужен ему до того, как планировщик до неё
              дойдёт. Найдено пробой в браузере 14 сентября 2026: кнопка стояла
              под условием «тема открыта» и на нужной теме не появилась.
            */}
            {chosen.theory ? (
              <button className="btn" onClick={() => onOpenTheory(chosen.skillId, chosen.open)}>
                Разобраться
              </button>
            ) : null}

            {/* У открытой темы тренажёр стоит после «Решать»: там задачи идут
                в счёт дня и двигают владение, и это сильнее тренировки. */}
            {chosen.open ? (
              <button className="btn" disabled={busy} onClick={() => onDrill(chosen.skillId)}>
                Тренажёр
              </button>
            ) : null}

            {chosen.open && !locked && topicDay?.skillId !== chosen.skillId ? (
              <button
                className="btn"
                disabled={busy}
                onClick={() =>
                  run(
                    ensurePlan({
                      learnerKey,
                      mode: "topic",
                      topicSkillId: chosen.skillId,
                    }),
                    () => setPicked(null),
                  )
                }
              >
                Весь день на эту тему
              </button>
            ) : null}
          </div>

          {/*
            Цена режима названа до нажатия и числом. Запрета нет и не будет —
            решение от 2 сентября 2026 выбрало показывать цену вместо
            блокировки, — но цена обязана быть на экране, а не в документации.
          */}
          {chosen.open && !locked && topicDay?.skillId !== chosen.skillId ? (
            <div className="muted" style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6 }}>
              День по теме — это двенадцать задач по ней одной.{" "}
              {reviewsLeft > 0 ? (
                <>
                  <span className="num">{reviewsLeft}</span>{" "}
                  {plural(reviewsLeft, "повторение", "повторения", "повторений")} из
                  сегодняшнего набора уедут на завтра и там никуда не денутся.
                </>
              ) : (
                <>Повторения по другим темам сегодня не придут.</>
              )}
            </div>
          ) : null}
        </>
      )}

      {failed === null ? null : (
        <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
          {failed}
        </div>
      )}
    </div>
  );
}
